// Product Card Multi-Image Preview JavaScript
// Enhances product cards with interactive image cycling

// DEBUGGING: Check if this file loads
console.log('🔧 DEBUG: product-card-preview.js file loaded');
console.log('🔧 DEBUG: Current URL:', window.location.href);
console.log('🔧 DEBUG: Screen width:', window.innerWidth);

// Debug: Check for any JavaScript errors
window.addEventListener('error', function(e) {
  console.error('🚨 DEBUG: JavaScript Error:', e.error);
  console.error('🚨 DEBUG: Error location:', e.filename + ':' + e.lineno);
});

// Debug: Check DOM state
console.log('🔧 DEBUG: Document ready state:', document.readyState);
console.log('🔧 DEBUG: DOM element counts at load:');
console.log('  - Total elements:', document.querySelectorAll('*').length);
console.log('  - .card elements:', document.querySelectorAll('.card').length);
console.log('  - Script elements:', document.querySelectorAll('script').length);

class ProductCardPreview {
  constructor(cardElement) {
    console.log('🔧 DEBUG: ProductCardPreview constructor called');
    console.log('🔧 DEBUG: cardElement:', cardElement);
    
    this.card = cardElement;
    this.productId = cardElement.dataset.productId;
    this.imageCount = parseInt(cardElement.dataset.imageCount) || 0;
    this.previewStyle = cardElement.dataset.previewStyle || 'dots';
    this.previewSpeed = parseInt(cardElement.dataset.previewSpeed) || 2000;
    
    this.container = cardElement.querySelector('.product-preview-container');
    this.images = Array.from(cardElement.querySelectorAll('.preview-image'));
    this.dots = Array.from(cardElement.querySelectorAll('.preview-dot'));
    this.arrows = Array.from(cardElement.querySelectorAll('.preview-arrow'));
    
    // Clean initialization without debug logs
    
    this.currentIndex = 0;
    this.autoInterval = null;
    this.isHovering = false;
    this.isTouching = false;
    
    // Touch handling
    this.touchStartX = 0;
    this.touchStartY = 0;
    this.touchEndX = 0;
    this.touchEndY = 0;
    
    this.init();
  }
  
  init() {
    console.log(`Init called for product ${this.productId}: imageCount=${this.imageCount}, container=${!!this.container}, images=${this.images.length}, dots=${this.dots.length}`);
    
    if (this.imageCount <= 1 || !this.container) {
      console.log(`Skipping init: imageCount=${this.imageCount}, container=${!!this.container}`);
      return;
    }
    
    // Check if we're on mobile
    this.isMobile = window.innerWidth <= 749;
    
    this.setupEventListeners();
    this.setupAccessibility();
    
    if (this.isMobile) {
      // On mobile, set up horizontal scrolling behavior
      this.setupMobileScrolling();
    } else {
      // On desktop, initialize fade-based preview
      this.setupDesktopPreview();
    }
    
    // Handle window resize
    window.addEventListener('resize', () => this.handleResize());
    
    console.log(`ProductCardPreview initialized for product ${this.productId}: ${this.imageCount} images, mobile: ${this.isMobile}, style: ${this.previewStyle}`);
  }
  
  handleResize() {
    const wasMobile = this.isMobile;
    this.isMobile = window.innerWidth <= 749;
    
    if (wasMobile !== this.isMobile) {
      // Behavior changed, reinitialize
      
      if (this.isMobile) {
        // Switched to mobile
        this.setupMobileScrolling();
      } else {
        // Switched to desktop - clean up mobile arrows
        this.cleanupMobileArrows();
        this.setupDesktopPreview();
      }
    }
  }
  
  cleanupMobileArrows() {
    // Remove any mobile arrows when switching to desktop
    if (this.container) {
      const mobileArrows = this.container.querySelectorAll('.mobile-arrow, .mobile-arrows');
      mobileArrows.forEach(arrow => arrow.remove());
    }
  }
  
  setupCSSArrows() {
    // Setup click handlers for CSS arrows
    const arrows = this.container.querySelectorAll('.preview-arrow');
    
    console.log(`🏹 Setting up ${arrows.length} CSS arrows for container:`, this.container);
    
    arrows.forEach((arrow, index) => {
      console.log(`🏹 Arrow ${index}:`, arrow, 'Classes:', arrow.className);
      
      arrow.addEventListener('click', (e) => {
        console.log('🎯 ARROW CLICKED!', e.target, 'Event:', e);
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        
        const isPrev = arrow.classList.contains('preview-arrow--prev');
        console.log(`🏹 Arrow direction: ${isPrev ? 'PREV' : 'NEXT'}`);
        console.log(`🏹 Current index: ${this.currentIndex}, Total images: ${this.imageCount}`);
        
        if (isPrev) {
          // Previous image
          const newIndex = this.currentIndex > 0 ? this.currentIndex - 1 : this.imageCount - 1;
          console.log(`🏹 Going to PREV image: ${newIndex}`);
          this.goToImage(newIndex);
        } else {
          // Next image
          const newIndex = (this.currentIndex + 1) % this.imageCount;
          console.log(`🏹 Going to NEXT image: ${newIndex}`);
          this.goToImage(newIndex);
        }
        
        return false;
      });
      
      // Also add touch events for mobile
      arrow.addEventListener('touchend', (e) => {
        console.log('📱 ARROW TOUCHED!', e.target, 'Event:', e);
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        
        const isPrev = arrow.classList.contains('preview-arrow--prev');
        console.log(`📱 Touch direction: ${isPrev ? 'PREV' : 'NEXT'}`);
        
        if (isPrev) {
          const newIndex = this.currentIndex > 0 ? this.currentIndex - 1 : this.imageCount - 1;
          console.log(`📱 Touch going to PREV image: ${newIndex}`);
          this.goToImage(newIndex);
        } else {
          const newIndex = (this.currentIndex + 1) % this.imageCount;
          console.log(`📱 Touch going to NEXT image: ${newIndex}`);
          this.goToImage(newIndex);
        }
        
        return false;
      });
      
      // Add mousedown for immediate feedback
      arrow.addEventListener('mousedown', (e) => {
        console.log('🖱️ ARROW MOUSEDOWN!', e.target);
      });
      
      // Add touchstart for immediate feedback
      arrow.addEventListener('touchstart', (e) => {
        console.log('👆 ARROW TOUCHSTART!', e.target);
      });
    });
  }
  
  setupClickZones() {
    // Setup click zones on both desktop and mobile
    
    const clickZones = this.container.querySelectorAll('.click-zone');
    
    clickZones.forEach(zone => {
      zone.addEventListener('click', (e) => {
        const action = zone.dataset.action;
        
        e.preventDefault();
        e.stopPropagation();
        
        if (action === 'prev') {
          // Green zone - Previous image
          if (this.images.length > 1) {
            const currentIndex = this.currentIndex;
            const newIndex = currentIndex > 0 ? currentIndex - 1 : this.imageCount - 1;
            this.goToImage(newIndex);
          }
        } else if (action === 'next') {
          // Green zone - Next image
          if (this.images.length > 1) {
            const currentIndex = this.currentIndex;
            const newIndex = (currentIndex + 1) % this.imageCount;
            this.goToImage(newIndex);
          }
        } else if (action === 'product') {
          // Red zone - Redirect to product page
          const productLink = this.card.querySelector('a[href*="/products/"]');
          if (productLink) {
            window.location.href = productLink.href;
          }
        }
        
        return false;
      });
    });
  }
  
  setupArrowFallback() {
    // If no arrows found in DOM, use click zones for arrow functionality
    console.log('🔧 DEBUG: Setting up arrow fallback using click zones');
    
    const prevZone = this.container.querySelector('.click-zone--prev');
    const nextZone = this.container.querySelector('.click-zone--next');
    
    if (prevZone) {
      prevZone.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (this.images.length > 1) {
          const currentIndex = this.currentIndex;
          const newIndex = currentIndex > 0 ? currentIndex - 1 : this.imageCount - 1;
          this.goToImage(newIndex);
        }
        
        return false;
      });
      console.log('🔧 DEBUG: PREV zone fallback handler added');
    }
    
    if (nextZone) {
      nextZone.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (this.images.length > 1) {
          const currentIndex = this.currentIndex;
          const newIndex = (currentIndex + 1) % this.imageCount;
          this.goToImage(newIndex);
        }
        
        return false;
      });
      console.log('🔧 DEBUG: NEXT zone fallback handler added');
    }
  }
  
  setupMobileScrolling() {
    // Mobile: Use same logic as desktop but with touch-friendly behavior
    // Show one image at a time with dots and arrows
    
    // Set up images for single display (like desktop)
    this.images.forEach((img, index) => {
      if (index === 0) {
        img.classList.add('preview-image--active');
        img.setAttribute('aria-hidden', 'false');
      } else {
        img.classList.remove('preview-image--active');
        img.setAttribute('aria-hidden', 'true');
      }
    });
    
    // Update dots to show current image
    this.updateDots();
    
    // Use CSS arrows on mobile too (same white arrows as desktop)
  }
  
  updateDots() {
    // Update dots to reflect current image
    console.log(`🔵 updateDots called - ${this.dots.length} dots available`);
    if (this.dots.length === 0) {
      console.log(`❌ No dots found to update`);
      return;
    }
    
    // Remove active class from all dots
    this.dots.forEach((dot, index) => {
      console.log(`🔵 Removing active from dot ${index}:`, dot);
      dot.classList.remove('preview-dot--active');
    });
    
    // Add active class to current dot
    if (this.dots[this.currentIndex]) {
      console.log(`🔵 Adding active to dot ${this.currentIndex}:`, this.dots[this.currentIndex]);
      this.dots[this.currentIndex].classList.add('preview-dot--active');
    } else {
      console.log(`❌ No dot found at currentIndex ${this.currentIndex}`);
    }
  }
  
  setupMobileArrows() {
    // Only create mobile arrows on mobile devices
    if (!this.container || !this.isMobile) return;
    
    // Remove any existing mobile arrows
    const existingArrows = this.container.querySelectorAll('.mobile-arrow');
    existingArrows.forEach(arrow => arrow.remove());
    
    // Create arrow container
    const arrowContainer = document.createElement('div');
    arrowContainer.className = 'mobile-arrows';
    
    // Create previous arrow
    const prevArrow = document.createElement('button');
    prevArrow.className = 'mobile-arrow mobile-arrow--prev';
    prevArrow.innerHTML = '<svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor"><path d="M12 16L8 12L12 8" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    prevArrow.setAttribute('aria-label', 'Previous image');
    
    // Create next arrow
    const nextArrow = document.createElement('button');
    nextArrow.className = 'mobile-arrow mobile-arrow--next';
    nextArrow.innerHTML = '<svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor"><path d="M8 4L12 8L8 12" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    nextArrow.setAttribute('aria-label', 'Next image');
    
    // Add click handlers
    prevArrow.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      
      if (this.images.length > 1) {
        const newIndex = this.currentIndex > 0 ? this.currentIndex - 1 : this.imageCount - 1;
        this.goToImage(newIndex);
      }
      
      return false;
    });
    
    nextArrow.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      
      if (this.images.length > 1) {
        const newIndex = (this.currentIndex + 1) % this.imageCount;
        this.goToImage(newIndex);
      }
      
      return false;
    });
    
    // Add arrows to container
    arrowContainer.appendChild(prevArrow);
    arrowContainer.appendChild(nextArrow);
    this.container.appendChild(arrowContainer);
  }
  
  setupDesktopPreview() {
    // Initialize based on preview style for desktop
    switch (this.previewStyle) {
      case 'auto':
        this.startAutoCycle();
        break;
      case 'hover':
        // Hover cycling will be handled by mouse events
        break;
      case 'dots':
      default:
        // Dots are already visible, no additional setup needed
        break;
    }
  }
  
  updateDotsFromScroll() {
    if (!this.isMobile || this.imageCount <= 1) return;
    
    const scrollLeft = this.container.scrollLeft;
    const imageWidth = this.container.clientWidth;
    const newIndex = Math.round(scrollLeft / imageWidth);
    
    if (newIndex !== this.currentIndex && newIndex >= 0 && newIndex < this.imageCount) {
      this.currentIndex = newIndex;
      
      // Update dots without triggering image change
      this.updateDots();
    }
  }
  
  setupEventListeners() {
    // Hover events
    this.card.addEventListener('mouseenter', () => this.handleMouseEnter());
    this.card.addEventListener('mouseleave', () => this.handleMouseLeave());
    
    // Click zone handling
    this.setupClickZones();
    
    // CSS Arrow click handling
    this.setupCSSArrows();
    
    // Dots container click handling removed - dots area not clickable for product
    
    // Dot navigation with aggressive click prevention + mobile touch support
    this.dots.forEach((dot, index) => {
      // Universal click handling for both desktop and mobile
      const handleDotClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        this.goToImage(index);
        return false;
      };
      
      // Add multiple event types for maximum compatibility
      dot.addEventListener('click', handleDotClick);
      dot.addEventListener('touchend', handleDotClick, { passive: false });
      
      // Also prevent mousedown/mouseup to be extra sure
      dot.addEventListener('mousedown', (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        return false;
      });
      
      dot.addEventListener('mouseenter', () => {
        if (this.previewStyle === 'hover' && this.isHovering) {
          this.goToImage(index);
        }
      });
    });
    
    // Arrow navigation setup
    if (this.arrows.length === 0) {
      // Try to find arrows within this card
      const cardArrows = this.card.querySelectorAll('.preview-arrow');
      
      if (cardArrows.length > 0) {
        this.arrows = Array.from(cardArrows);
      } else {
        // Set up click zone handlers as backup
        this.setupArrowFallback();
        return;
      }
    }
    
    this.arrows.forEach((arrow, index) => {
      // Use direct onclick property instead of addEventListener
      if (arrow.classList.contains('preview-arrow--prev')) {
        arrow.onclick = (e) => {
          e.preventDefault();
          e.stopPropagation();
          // Previous image
          const currentIndex = this.currentIndex;
          const newIndex = currentIndex > 0 ? currentIndex - 1 : this.imageCount - 1;
          this.goToImage(newIndex);
          return false;
        };
      } else if (arrow.classList.contains('preview-arrow--next')) {
        arrow.onclick = (e) => {
          e.preventDefault();
          e.stopPropagation();
          // Next image
          const currentIndex = this.currentIndex;
          const newIndex = (currentIndex + 1) % this.imageCount;
          this.goToImage(newIndex);
          return false;
        };
      }
      
      // Arrow setup complete
    });
    
    // Touch events for swipe
    if (this.container) {
      this.container.addEventListener('touchstart', (e) => this.handleTouchStart(e), { passive: true });
      this.container.addEventListener('touchend', (e) => this.handleTouchEnd(e), { passive: true });
    }
    
    // Keyboard navigation
    this.card.addEventListener('keydown', (e) => this.handleKeyDown(e));
    
    // Prevent interference with card link
    this.container.addEventListener('click', (e) => {
      if (e.target.closest('.preview-dot') || e.target.closest('.preview-arrow')) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
      }
    });
    
    // No duplicate click prevention needed - handled above
  }
  
  setupAccessibility() {
    // Add ARIA labels and roles
    this.container.setAttribute('role', 'region');
    this.container.setAttribute('aria-label', `Product images (${this.imageCount} available)`);
    
    this.images.forEach((img, index) => {
      img.setAttribute('aria-hidden', index !== this.currentIndex ? 'true' : 'false');
    });
    
    this.dots.forEach((dot, index) => {
      dot.setAttribute('role', 'button');
      dot.setAttribute('tabindex', '0');
      dot.setAttribute('aria-label', `View image ${index + 1} of ${this.imageCount}`);
    });
  }
  
  handleMouseEnter() {
    this.isHovering = true;
    this.card.classList.add('preview-active');
    
    if (this.previewStyle === 'hover' && this.imageCount > 1) {
      this.startHoverCycle();
    }
  }
  
  handleMouseLeave() {
    this.isHovering = false;
    this.card.classList.remove('preview-active');
    
    if (this.previewStyle === 'hover') {
      this.stopHoverCycle();
      this.goToImage(0); // Return to first image
    }
  }
  
  handleTouchStart(e) {
    this.isTouching = true;
    this.touchStartX = e.touches[0].clientX;
    this.touchStartY = e.touches[0].clientY;
  }
  
  handleTouchEnd(e) {
    if (!this.isTouching) return;
    
    this.isTouching = false;
    this.touchEndX = e.changedTouches[0].clientX;
    this.touchEndY = e.changedTouches[0].clientY;
    
    this.handleSwipe();
  }
  
  handleSwipe() {
    const deltaX = this.touchEndX - this.touchStartX;
    const deltaY = this.touchEndY - this.touchStartY;
    const minSwipeDistance = 50;
    
    // Check if horizontal swipe is more significant than vertical
    if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > minSwipeDistance) {
      if (deltaX > 0) {
        this.goToPrevious();
      } else {
        this.goToNext();
      }
    }
  }
  
  handleKeyDown(e) {
    if (!this.card.matches(':hover') && !this.card.matches(':focus-within')) return;
    
    switch (e.key) {
      case 'ArrowLeft':
        e.preventDefault();
        this.goToPrevious();
        break;
      case 'ArrowRight':
        e.preventDefault();
        this.goToNext();
        break;
      case ' ':
      case 'Enter':
        if (e.target.classList.contains('preview-dot')) {
          e.preventDefault();
          const index = Array.from(this.dots).indexOf(e.target);
          this.goToImage(index);
        }
        break;
    }
  }
  
  startAutoCycle() {
    this.stopAutoCycle(); // Clear any existing interval
    
    this.autoInterval = setInterval(() => {
      if (!this.isHovering && !this.isTouching) {
        this.goToNext();
      }
    }, this.previewSpeed);
  }
  
  stopAutoCycle() {
    if (this.autoInterval) {
      clearInterval(this.autoInterval);
      this.autoInterval = null;
    }
  }
  
  startHoverCycle() {
    this.hoverInterval = setInterval(() => {
      if (this.isHovering) {
        this.goToNext();
      }
    }, 1000); // Faster cycling on hover
  }
  
  stopHoverCycle() {
    if (this.hoverInterval) {
      clearInterval(this.hoverInterval);
      this.hoverInterval = null;
    }
  }
  
  goToImage(index) {
    console.log(`🖼️ goToImage called with index: ${index}`);
    console.log(`🖼️ Current index: ${this.currentIndex}, Image count: ${this.imageCount}`);
    console.log(`🖼️ Is mobile: ${this.isMobile}`);
    
    if (index < 0 || index >= this.imageCount || index === this.currentIndex) {
      console.log(`❌ goToImage early return - invalid index or same index`);
      return;
    }
    
    // Use fade behavior for both mobile and desktop
    console.log(`🖼️ Fade mode: switching to image ${index}`);
    
    // Add safety checks to prevent errors
    if (this.images[this.currentIndex]) {
      console.log(`🖼️ Hiding current image at index ${this.currentIndex}`);
      this.images[this.currentIndex].classList.remove('preview-image--active');
      this.images[this.currentIndex].setAttribute('aria-hidden', 'true');
    }
    
    if (this.images[index]) {
      console.log(`🖼️ Showing new image at index ${index}:`, this.images[index]);
      console.log(`🖼️ Image classes before:`, this.images[index].className);
      this.images[index].classList.add('preview-image--active');
      this.images[index].setAttribute('aria-hidden', 'false');
      console.log(`🖼️ Image classes after:`, this.images[index].className);
    } else {
      console.log(`❌ No image found at index ${index}`);
    }
    
    this.currentIndex = index;
    console.log(`🖼️ Updated currentIndex to ${this.currentIndex}`);
    
    // Update dots to reflect new current image
    this.updateDots();
    console.log(`🖼️ Dots updated, active dot should be at index ${index}`);
    
    // Announce to screen readers
    this.announceImageChange();
  }
  
  goToNext() {
    const nextIndex = (this.currentIndex + 1) % this.imageCount;
    this.goToImage(nextIndex);
  }
  
  goToPrevious() {
    const prevIndex = (this.currentIndex - 1 + this.imageCount) % this.imageCount;
    this.goToImage(prevIndex);
  }
  
  announceImageChange() {
    // Create or update ARIA live region for screen readers
    let liveRegion = this.card.querySelector('.preview-live-region');
    if (!liveRegion) {
      liveRegion = document.createElement('div');
      liveRegion.className = 'preview-live-region visually-hidden';
      liveRegion.setAttribute('aria-live', 'polite');
      liveRegion.setAttribute('aria-atomic', 'true');
      this.card.appendChild(liveRegion);
    }
    
    liveRegion.textContent = `Showing image ${this.currentIndex + 1} of ${this.imageCount}`;
  }
  
  destroy() {
    this.stopAutoCycle();
    this.stopHoverCycle();
    // Remove event listeners would go here if needed
  }
}

console.log('🔧 DEBUG: Setting up initialization...');
console.log('🔧 DEBUG: Document ready state:', document.readyState);

// Initialize all product card previews when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  console.log('🔧 DEBUG: DOMContentLoaded fired');
  initializeProductCardPreviews();
});

// Also initialize when new content is loaded (for infinite scroll, etc.)
document.addEventListener('shopify:section:load', () => {
  console.log('🔧 DEBUG: shopify:section:load event fired');
  initializeProductCardPreviews();
});

// Try to initialize immediately if DOM is already ready
if (document.readyState === 'complete' || document.readyState === 'interactive') {
  console.log('🔧 DEBUG: DOM already ready, initializing immediately');
  setTimeout(initializeProductCardPreviews, 100);
}

// Optimized resize handler - minimal logging
let resizeTimeout;
window.addEventListener('resize', () => {
  clearTimeout(resizeTimeout);
  resizeTimeout = setTimeout(() => {
    // Remove existing floating arrows
    document.querySelectorAll('.floating-arrow').forEach(el => el.remove());
    
    // Re-setup arrows if we're on mobile
    if (window.innerWidth <= 749) {
      setTimeout(() => {
        if (typeof window.setupArrowClicks === 'function') {
          window.setupArrowClicks();
        }
      }, 100);
    }
    
    // Force dots to be visible
    const allDots = document.querySelectorAll('.preview-dots');
    allDots.forEach(dotsContainer => {
      dotsContainer.style.display = 'flex';
      dotsContainer.style.opacity = '1';
      dotsContainer.style.pointerEvents = 'all';
    });
    
  }, 300); // Slightly longer debounce to reduce events
});

function initializeProductCardPreviews() {
  try {
    console.log('🔧 DEBUG: initializeProductCardPreviews() called');
    
    const cards = document.querySelectorAll('.card--preview-enabled');
    const allCards = document.querySelectorAll('.card');
    const enhancedCards = document.querySelectorAll('[data-product-id]');
    const previewContainers = document.querySelectorAll('.product-preview-container');
  
  console.log('🔧 DEBUG: DOM Query Results:');
  console.log(`  - Total .card elements: ${allCards.length}`);
  console.log(`  - .card--preview-enabled: ${cards.length}`);
  console.log(`  - [data-product-id]: ${enhancedCards.length}`);
  console.log(`  - .product-preview-container: ${previewContainers.length}`);
  
  // Debug: Check if the enhanced template is being used
  if (allCards.length > 0 && enhancedCards.length === 0) {
    console.error('🚨 DEBUG: No [data-product-id] found! The enhanced template may not be loading.');
    console.log('🔧 DEBUG: First card HTML:', allCards[0].outerHTML.substring(0, 500));
  }
  
  // Debug: Log the first few cards in detail
  allCards.forEach((card, index) => {
    if (index < 3) {
      const productId = card.dataset.productId;
      const imageCount = card.dataset.imageCount;
      const hasPreviewClass = card.classList.contains('card--preview-enabled');
      const container = card.querySelector('.product-preview-container');
      const images = card.querySelectorAll('.preview-image');
      const dots = card.querySelectorAll('.preview-dot');
      const arrows = card.querySelectorAll('.preview-arrow');
      
      console.log(`🔧 DEBUG: Card ${index}:`);
      console.log(`  - productId: ${productId}`);
      console.log(`  - imageCount: ${imageCount}`);
      console.log(`  - hasPreviewClass: ${hasPreviewClass}`);
      console.log(`  - container: ${!!container}`);
      console.log(`  - images: ${images.length}`);
      console.log(`  - dots: ${dots.length}`);
      console.log(`  - arrows: ${arrows.length}`);
      console.log(`  - classes: ${card.className}`);
    }
  });
  
  cards.forEach(card => {
    // Avoid double initialization
    if (card.hasAttribute('data-preview-initialized')) {
      console.log('🔧 DEBUG: Card already initialized, skipping');
      return;
    }
    
    console.log('🔧 DEBUG: Creating ProductCardPreview for card:', card);
    card.setAttribute('data-preview-initialized', 'true');
    const previewInstance = new ProductCardPreview(card);
    
    // Store the instance on the container for arrow access
    const container = card.querySelector('.product-preview-container');
    if (container) {
      container.productCardPreview = previewInstance;
    }
  });
  
  console.log(`🔧 DEBUG: Initialized ${cards.length} product card previews`);
  
  // Debug: Check CSS loading
  const bodyStyle = window.getComputedStyle(document.body);
  const cssLoaded = bodyStyle.getPropertyValue('--debug-css-loaded');
  console.log('🔧 DEBUG: CSS loaded check:', cssLoaded === 'true' ? 'YES' : 'NO');
  
  const testElement = document.createElement('div');
  testElement.className = 'preview-dot';
  testElement.style.position = 'absolute';
  testElement.style.top = '-9999px';
  document.body.appendChild(testElement);
  
  const computedStyle = window.getComputedStyle(testElement);
  console.log('🔧 DEBUG: CSS Check - preview-dot border-radius:', computedStyle.borderRadius);
  console.log('🔧 DEBUG: CSS Check - preview-dot z-index:', computedStyle.zIndex);
  
  document.body.removeChild(testElement);
  
  // FORCE all dots to be visible immediately
  console.log('🔧 DEBUG: Forcing all dots to be visible...');
  const allDots = document.querySelectorAll('.preview-dots');
  allDots.forEach(dotsContainer => {
    dotsContainer.style.display = 'flex !important';
    dotsContainer.style.opacity = '1 !important';
    dotsContainer.style.pointerEvents = 'all !important';
    dotsContainer.style.visibility = 'visible !important';
  });
  
  // Setup arrows for mobile (only if we're on mobile)
  if (window.innerWidth <= 749) {
    console.log('🔧 DEBUG: Mobile detected, setting up arrows...');
    setTimeout(() => {
      if (typeof window.setupArrowClicks === 'function') {
        window.setupArrowClicks();
      }
    }, 500); // Small delay to ensure all elements are rendered
  }
  
  } catch (error) {
    console.error('🚨 DEBUG: Error in initializeProductCardPreviews:', error);
    console.error('🚨 DEBUG: Error stack:', error.stack);
  }
}

// Intersection Observer for performance (only activate when visible)
if ('IntersectionObserver' in window) {
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      const card = entry.target;
      const previewInstance = card.previewInstance;
      
      if (entry.isIntersecting) {
        // Card is visible - enable auto-cycling if configured
        if (previewInstance && previewInstance.previewStyle === 'auto') {
          previewInstance.startAutoCycle();
        }
      } else {
        // Card is not visible - pause auto-cycling to save performance
        if (previewInstance) {
          previewInstance.stopAutoCycle();
        }
      }
    });
  }, {
    rootMargin: '50px',
    threshold: 0.1
  });
  
  // Observe all preview-enabled cards
  document.addEventListener('DOMContentLoaded', () => {
    setTimeout(() => {
      const cards = document.querySelectorAll('.card--preview-enabled');
      cards.forEach(card => observer.observe(card));
    }, 100);
  });
}

// Utility function to update preview settings dynamically
window.updateProductCardPreview = function(productId, settings) {
  const card = document.querySelector(`[data-product-id="${productId}"]`);
  if (card && card.previewInstance) {
    Object.assign(card.previewInstance, settings);
    console.log(`Updated preview settings for product ${productId}:`, settings);
  }
};

// Add styles for visually-hidden class if not already present
if (!document.querySelector('style[data-preview-styles]')) {
  const style = document.createElement('style');
  style.setAttribute('data-preview-styles', 'true');
  style.textContent = `
    .visually-hidden {
      position: absolute !important;
      width: 1px !important;
      height: 1px !important;
      padding: 0 !important;
      margin: -1px !important;
      overflow: hidden !important;
      clip: rect(0, 0, 0, 0) !important;
      white-space: nowrap !important;
      border: 0 !important;
    }
  `;
  document.head.appendChild(style);
}

// Export for module systems
if (typeof module !== 'undefined' && module.exports) {
  module.exports = ProductCardPreview;
}

// Global reference
window.ProductCardPreview = ProductCardPreview;

// Mobile Debug Functions
window.mobileDebug = {
  // Toggle mobile debug mode
  toggleDebug: function() {
    const debugStyle = document.getElementById('mobile-debug-style');
    if (debugStyle) {
      debugStyle.remove();
      console.log('📱 Mobile debug: OFF');
    } else {
      const style = document.createElement('style');
      style.id = 'mobile-debug-style';
      style.textContent = `
        @media screen and (max-width: 749px) {
          .mobile-arrows { border: 2px solid lime !important; }
          .mobile-arrow { border: 3px solid red !important; }
          .preview-dots { border: 2px solid blue !important; }
          .preview-dot { border: 2px solid yellow !important; }
        }
      `;
      document.head.appendChild(style);
      console.log('📱 Mobile debug: ON - Green=arrows container, Red=arrow buttons, Blue=dots container, Yellow=dots');
    }
  },
  
  // Test mobile arrows
  testArrows: function() {
    const containers = document.querySelectorAll('.product-preview-container');
    console.log(`📱 Testing ${containers.length} mobile containers...`);
    
    containers.forEach((container, index) => {
      const arrows = container.querySelectorAll('.mobile-arrow');
      const dots = container.querySelectorAll('.preview-dot');
      console.log(`📱 Container ${index}: ${arrows.length} arrows, ${dots.length} dots`);
    });
  },
  
  // Force mobile mode
  forceMobile: function() {
    document.body.style.width = '375px';
    window.dispatchEvent(new Event('resize'));
    console.log('📱 Forced mobile viewport (375px)');
  },
  
  // Reset viewport
  resetViewport: function() {
    document.body.style.width = '';
    window.dispatchEvent(new Event('resize'));
    console.log('📱 Reset viewport');
  },
  
  // Remove any fixed position arrows
  removeFixedArrows: function() {
    const fixedArrows = document.querySelectorAll('.floating-arrow');
    fixedArrows.forEach(arrow => arrow.remove());
    console.log(`🗑️ Removed ${fixedArrows.length} fixed position arrows`);
  },
  
  // Check for arrow conflicts
  checkArrows: function() {
    const containers = document.querySelectorAll('.product-preview-container');
    console.log(`🔍 Checking ${containers.length} containers for arrow conflicts...`);
    
    containers.forEach((container, index) => {
      const cssArrows = container.querySelectorAll('.preview-arrow');
      const mobileArrows = container.querySelectorAll('.mobile-arrow');
      const cssArrowsVisible = cssArrows.length > 0 && window.getComputedStyle(cssArrows[0]).display !== 'none';
      const mobileArrowsVisible = mobileArrows.length > 0 && window.getComputedStyle(mobileArrows[0]).display !== 'none';
      
      console.log(`📦 Container ${index}:`);
      console.log(`  - CSS arrows: ${cssArrows.length} (visible: ${cssArrowsVisible})`);
      console.log(`  - Mobile arrows: ${mobileArrows.length} (visible: ${mobileArrowsVisible})`);
      
      if (cssArrowsVisible && mobileArrowsVisible) {
        console.warn(`⚠️  CONFLICT: Both arrow types visible in container ${index}!`);
      }
    });
  }
};

// Disabled: No longer creating fixed position arrows - using CSS arrows everywhere
window.setupArrowClicks = function() {
  // DISABLED: We now use CSS arrows on both desktop and mobile
  console.log('📱 setupArrowClicks disabled - using CSS arrows everywhere');
  return;
  
  // Remove any existing floating arrows
  document.querySelectorAll('.floating-arrow').forEach(el => el.remove());
  
  const containers = document.querySelectorAll('.product-preview-container');
  
  containers.forEach((container, containerIndex) => {
    const containerRect = container.getBoundingClientRect();
    
    // Create PREV arrow with proper styling
    const prevArrow = document.createElement('button');
    prevArrow.className = `floating-arrow floating-prev-${containerIndex}`;
    prevArrow.innerHTML = '<svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor"><path d="M10 12L6 8L10 4" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    
    const prevLeft = containerRect.left + 10;
    const prevTop = containerRect.top + containerRect.height/2 - 22;
    
    prevArrow.style.cssText = `
      position: fixed !important;
      left: ${prevLeft}px !important;
      top: ${prevTop}px !important;
      width: 44px !important;
      height: 44px !important;
      background: rgba(0, 0, 0, 0.8) !important;
      color: white !important;
      border: 2px solid rgba(255, 255, 255, 0.8) !important;
      border-radius: 50% !important;
      z-index: 2147483647 !important;
      pointer-events: all !important;
      cursor: pointer !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      backdrop-filter: blur(10px) !important;
      user-select: none !important;
      transition: all 0.2s ease !important;
    `;
    
    // Create NEXT arrow with proper styling
    const nextArrow = document.createElement('button');
    nextArrow.className = `floating-arrow floating-next-${containerIndex}`;
    nextArrow.innerHTML = '<svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor"><path d="M6 4L10 8L6 12" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    
    const nextLeft = containerRect.right - 54;
    const nextTop = containerRect.top + containerRect.height/2 - 22;
    
    nextArrow.style.cssText = `
      position: fixed !important;
      left: ${nextLeft}px !important;
      top: ${nextTop}px !important;
      width: 44px !important;
      height: 44px !important;
      background: rgba(0, 0, 0, 0.8) !important;
      color: white !important;
      border: 2px solid rgba(255, 255, 255, 0.8) !important;
      border-radius: 50% !important;
      z-index: 2147483647 !important;
      pointer-events: all !important;
      cursor: pointer !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      backdrop-filter: blur(10px) !important;
      user-select: none !important;
      transition: all 0.2s ease !important;
    `;
    
    // Add hover effects
    prevArrow.onmouseenter = () => {
      prevArrow.style.background = 'rgba(0, 0, 0, 0.9)';
      prevArrow.style.transform = 'scale(1.1)';
    };
    prevArrow.onmouseleave = () => {
      prevArrow.style.background = 'rgba(0, 0, 0, 0.8)';
      prevArrow.style.transform = 'scale(1)';
    };
    
    nextArrow.onmouseenter = () => {
      nextArrow.style.background = 'rgba(0, 0, 0, 0.9)';
      nextArrow.style.transform = 'scale(1.1)';
    };
    nextArrow.onmouseleave = () => {
      nextArrow.style.background = 'rgba(0, 0, 0, 0.8)';
      nextArrow.style.transform = 'scale(1)';
    };
    
    // Simplified arrow click handlers
    function handlePrevClick(e) {
      const preview = container.productCardPreview;
      if (preview) {
        const currentIndex = preview.currentIndex;
        const newIndex = currentIndex > 0 ? currentIndex - 1 : preview.imageCount - 1;
        preview.goToImage(newIndex);
      }
      e.stopPropagation();
      e.preventDefault();
      return false;
    }
    
    function handleNextClick(e) {
      const preview = container.productCardPreview;
      if (preview) {
        const currentIndex = preview.currentIndex;
        const newIndex = (currentIndex + 1) % preview.imageCount;
        preview.goToImage(newIndex);
      }
      e.stopPropagation();
      e.preventDefault();
      return false;
    }
    
    // Multiple event handlers for maximum compatibility
    prevArrow.onmousedown = handlePrevClick;
    prevArrow.onclick = handlePrevClick;
    prevArrow.ontouchend = handlePrevClick;
    
    nextArrow.onmousedown = handleNextClick;
    nextArrow.onclick = handleNextClick;
    nextArrow.ontouchend = handleNextClick;
    
    // Add to body
    document.body.appendChild(prevArrow);
    document.body.appendChild(nextArrow);
    
    console.log(`🔧 DEBUG: Proper arrows created for container ${containerIndex}`);
  });
};
window.debugProductCards = function() {
  console.log('🔧 DEBUG: Manual debug function called');
  const cards = document.querySelectorAll('.card');
  const enhancedCards = document.querySelectorAll('[data-product-id]');
  const previewCards = document.querySelectorAll('.card--preview-enabled');
  const containers = document.querySelectorAll('.product-preview-container');
  const dots = document.querySelectorAll('.preview-dot');
  const arrows = document.querySelectorAll('.preview-arrow');
  
  console.log('🔧 DEBUG: Current DOM state:');
  console.log(`  - .card: ${cards.length}`);
  console.log(`  - [data-product-id]: ${enhancedCards.length}`);
  console.log(`  - .card--preview-enabled: ${previewCards.length}`);
  console.log(`  - .product-preview-container: ${containers.length}`);  
  console.log(`  - .preview-dot: ${dots.length}`);
  console.log(`  - .preview-arrow: ${arrows.length}`);
  
  // Try to re-initialize
  console.log('🔧 DEBUG: Attempting re-initialization...');
  initializeProductCardPreviews();
};

// Manual arrow creation function for debugging - ENHANCED VERSION
window.createTestArrows = function() {
  console.log('🔧 DEBUG: Creating test arrows manually...');
  const containers = document.querySelectorAll('.product-preview-container');
  
  containers.forEach((container, index) => {
    // Remove existing arrows
    const existingArrows = container.querySelector('.preview-arrows');
    if (existingArrows) {
      existingArrows.remove();
    }
    
    // Create new arrow container
    const arrowContainer = document.createElement('div');
    arrowContainer.className = 'preview-arrows';
    arrowContainer.style.cssText = `
      position: absolute;
      top: 50%;
      left: 0;
      right: 0;
      transform: translateY(-50%);
      z-index: 999999;
      pointer-events: none;
      display: block;
    `;
    
    // Create prev arrow
    const prevArrow = document.createElement('button');
    prevArrow.className = 'preview-arrow preview-arrow--prev';
    prevArrow.innerHTML = '◀';
    prevArrow.style.cssText = `
      position: absolute;
      left: 10px;
      background: red !important;
      color: white !important;
      border: 5px solid yellow !important;
      border-radius: 50% !important;
      width: 80px !important;
      height: 80px !important;
      z-index: 9999999 !important;
      pointer-events: all !important;
      cursor: pointer !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      font-size: 24px !important;
    `;
    
    // Create next arrow  
    const nextArrow = document.createElement('button');
    nextArrow.className = 'preview-arrow preview-arrow--next';
    nextArrow.innerHTML = '▶';
    nextArrow.style.cssText = `
      position: absolute;
      right: 10px;
      background: red !important;
      color: white !important;
      border: 5px solid yellow !important;
      border-radius: 50% !important;
      width: 80px !important;
      height: 80px !important;
      z-index: 9999999 !important;
      pointer-events: all !important;
      cursor: pointer !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      font-size: 24px !important;
    `;
    
    // Add simple direct click handlers that MUST work
    // Prev arrow click removed - using CSS arrows on desktop
    
    // Next arrow click removed - using CSS arrows on desktop
    
    arrowContainer.appendChild(prevArrow);
    arrowContainer.appendChild(nextArrow);
    container.appendChild(arrowContainer);
    
    console.log(`🔧 DEBUG: Created arrows for container ${index}`);
  });
  
  console.log('🔧 DEBUG: Manual arrows created with direct onclick handlers');
};
